import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import { EditOutlined } from "@ant-design/icons";
import * as Yup from "yup";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import getValidationErrors from "../../../utils/getValidationErrors";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";
import { IConfig } from "../../../pages/Opex";
import { IUser } from "../../../pages/User";
import { IAllocation } from "../../../pages/Dashboards/AllocationRectability";

interface IEditFieldModalProps {
  opexUpdate: IConfig;
  fieldName: string;
  handleConfig: (config: IConfig[]) => void;
  validationRule?: Yup.StringSchema<string>;
  title: string;
  exist_thirteenth_salary?: boolean;
  type_config?: string;
}

interface IEditFieldModalFormData {
  realized_opex: number;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const EditFieldModal: React.FC<IEditFieldModalProps> = ({
  opexUpdate,
  fieldName,
  handleConfig,
  validationRule,
  title,
  type_config,
  exist_thirteenth_salary = false,
}) => {
  const { t } = useTranslation(["config"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();
  const { user } = useAuth();

  const { addToast } = useToast();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role]
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Um erro occoreu ao alterar o campo, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao cancelar",
        description,
      });
    },
    [addToast]
  );

  useEffect(() => {
    if (opexUpdate) {
      form.setFieldsValue({
        realized_opex: opexUpdate.value,
      });
    }
  }, [opexUpdate]);

  const handleSendRequest = useCallback(
    async (data: IEditFieldModalFormData) => {
      setLoading(true);
      try {
        const payload = {
          name: type_config || "PLANNED_OPEX",
          value: data?.realized_opex,
          user_id: user?.id,
        };
        let response: AxiosResponse;
        if (opexUpdate) {
          response = await api.put<IConfig>(
            `/configurations/${opexUpdate?.id}`,
            payload
          );
        } else {
          response = await api.post<IConfig>("/configurations", payload);
        }
        handleConfig([response.data]);
        if (type_config === "INTEREST_RATE") {
          const rectability = await api.get("/allocation");
          if (rectability.data?.length) {
            rectability.data.map(async (userUpdate: IAllocation) => {
              await api.patch(`/allocation/${userUpdate.id}`, {
                tax: payload.value,
              });
            });
          }
        }
        if (!type_config || type_config === "PLANNED_OPEX") {
          const users = await api.get("/users");
          if (users.data?.length) {
            users.data.map(async (userUpdate: IUser) => {
              await api.patch(`/users/${userUpdate.id}/consultant`, {
                cost_operation: (payload.value / users.data.length).toFixed(2),
              });
            });
          }
        }

        handleClose();
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleClose, handleConfig]
  );

  const handleSubmit = useCallback(
    async (data: IEditFieldModalFormData) => {
      try {
        if (validationRule) {
          const shape = {} as any;
          shape[fieldName] = validationRule;
          const schema = Yup.object().shape(shape);

          await schema.validate(data, {
            abortEarly: false,
          });
        }
        await handleSendRequest(data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [fieldName, handleSendRequest, validationRule]
  );

  return isLoggedUserManager ? (
    <>
      <Modal
        // title={t("cancel")}
        title={`Editar ${title}`}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        forceRender
      >
        <Form
          {...layout}
          form={form}
          name="edit-field-form"
          onFinish={handleSubmit}
        >
          <FormItem
            name="realized_opex"
            label={t("config.value")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        </Form>
      </Modal>
      <Button
        type="link"
        shape="circle"
        icon={<EditOutlined />}
        onClick={() => setVisible((oldVisible) => !oldVisible)}
      />
    </>
  ) : (
    <></>
  );
};

export default EditFieldModal;
